import { z } from "zod";

import { setWithExpiry, getWithExpiry } from "app/utils/sessionStorage";
import { validateSchema } from "app/utils/validateSchema";

import type { Headers } from "react-relay-network-modern/lib";

export const SamlProviderSchema = z.object({
  name: z.string(),
  idp_id: z.string(),
  display_name: z.string().optional(),
});

export const SamlProvidersArraySchema = z.array(SamlProviderSchema);

export type SamlProvider = z.infer<typeof SamlProviderSchema>;

interface Configs {
  cognito: {
    cognito_install: string;
    cognito_user_pool_region: string;
    cognito_user_pool_id: string;
    cognito_user_pool_client_id: string;
    saml: Record<string, string>;
    saml_providers?: SamlProvider[];
  };

  cubejs: {
    cubejs_domain: string;
  };

  deployment: {
    gql_uri: string;
    headers?: Headers;
    // Local-dev only (`just local-console`): absolute URL of the local IdP's
    // token endpoint, which the console hits directly to mint a JWT.
    local_idp_token_url?: string;
  };

  docs?: {
    docs_url: string;
  };

  jun0?: {
    jun0_url: string;
  };

  redash: {
    redash_uri: string;
  };

  sinistral: {
    sinistral_url: string;
  };

  superset?: {
    superset_uri: string;
  };
}

const ConfigsSchema = z.object({
  cognito: z.object({
    cognito_install: z.string(),
    cognito_user_pool_region: z.string(),
    cognito_user_pool_id: z.string(),
    cognito_user_pool_client_id: z.string(),
    saml: z.record(z.string()),
    saml_providers: SamlProvidersArraySchema.optional(),
  }),
  cubejs: z.object({ cubejs_domain: z.string() }),
  deployment: z.object({
    gql_uri: z.string(),
    headers: z.record(z.string()).optional(),
    local_idp_token_url: z.string().optional(),
  }),
  docs: z.object({ docs_url: z.string() }).optional(),
  jun0: z.object({ jun0_url: z.string() }).optional(),
  redash: z.object({ redash_uri: z.string() }),
  sinistral: z.object({ sinistral_url: z.string() }),
  superset: z.object({ superset_uri: z.string() }).optional(),
}) satisfies z.ZodType<Configs>;

async function _fetchJun0Config(): Promise<Configs["jun0"] | undefined> {
  try {
    return await (await fetch("/config/jun0.json")).json();
  } catch {
    // Jun0 may not have config yet, which is ok.
    return undefined;
  }
}

async function _fetchSupersetConfig(): Promise<
  Configs["superset"] | undefined
> {
  try {
    return await (await fetch("/config/superset.json")).json();
  } catch {
    // Superset may not be deployed in this environment, which is ok.
    return undefined;
  }
}

async function _fetchConfigs(): Promise<Configs> {
  const fetchJson = async (path: string) => (await fetch(path)).json();
  const [cognito, cubejs, deployment, docs, jun0, redash, sinistral, superset] =
    await Promise.all([
      fetchJson("/config/cognito.json"),
      fetchJson("/config/cubejs.json"),
      fetchJson("/config/deployment.json"),
      fetchJson("/config/docs.json"),
      _fetchJun0Config(),
      fetchJson("/config/redash.json"),
      fetchJson("/config/sinistral.json"),
      _fetchSupersetConfig(),
    ]);
  const configs: Configs = {
    cognito,
    cubejs,
    deployment,
    docs,
    jun0,
    redash,
    sinistral,
    superset,
  };
  // Warn only — a slightly malformed config should not take down the console.
  // (No Sentry capture here: configs are fetched before Sentry.init runs.)
  const [, validationErrors] = validateSchema(ConfigsSchema, configs);
  if (validationErrors) {
    console.warn("Fetched configs failed validation:", validationErrors);
  }
  return configs;
}

// De-duplicates concurrent callers while a fetch is in flight; the resolved
// value is cached in sessionStorage, so the promise is dropped once settled.
let inflightConfigs: Promise<Configs> | null = null;

export async function getConfigs(): Promise<Configs> {
  const configsJSON = getWithExpiry("configs");
  if (configsJSON) {
    return JSON.parse(configsJSON);
  }
  inflightConfigs ??= _fetchConfigs()
    .then((configs) => {
      setWithExpiry("configs", JSON.stringify(configs), 60);
      return configs;
    })
    .finally(() => {
      inflightConfigs = null;
    });
  return inflightConfigs;
}
